import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, Card, Overline, PrimaryButton, GhostButton, StatusPill, Row, Avatar, C, Loader } from "@/components/district/kit";
import { DateField } from "@/components/DateField";
import { MagnifyingGlass, CaretRight, X, CalendarBlank, PaperPlaneTilt, ArrowClockwise, User, ClipboardText, Buildings, Plus } from "@phosphor-icons/react";

const clubStatus = (c) => (!c.visit ? "not_initiated" : c.visit.status === "closed" ? "completed" : "in_progress");
const validEmail = (e) => /.+@.+\..+/.test((e || "").trim());

function RecipientList({ recipients, onRemove, extra, setExtra, onAdd }) {
  return (
    <div data-testid="recipient-preview">
      {recipients.length === 0 && <p className="text-[14px]" style={{ color: C.sub }}>No recipients resolved yet.</p>}
      <div className="divide-y" style={{ borderColor: C.line }}>
        {recipients.map((r, i) => (
          <div key={r.email} className="flex items-center gap-3 py-2.5" data-testid={`recipient-${r.email}`}>
            <Avatar label={r.name} />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-medium" style={{ color: C.ink }}>{r.role}</span>
              <span className="block truncate text-[13px]" style={{ color: C.sub }}>{r.email}</span>
            </span>
            <button data-testid={`recipient-remove-${r.email}`} onClick={() => onRemove(i)} aria-label="Remove"
              className="grid h-9 w-9 place-items-center rounded-full transition-colors hover:bg-[#FDECEA]" style={{ color: C.cranberry }}>
              <X size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input data-testid="recipient-add-input" value={extra} onChange={(e) => setExtra(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()} placeholder="Add another email…"
          className="min-h-[44px] flex-1 rounded-2xl border bg-white px-3.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
        <button data-testid="recipient-add-btn" onClick={onAdd}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: C.azureSoft, color: C.azure }}>
          <Plus size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

export function ClubDrawer({ club, canInitiate, onClose, onChanged, onOpenReport, inline }) {
  const navigate = useNavigate();
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [invs, setInvs] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [extra, setExtra] = useState("");
  const [notifs, setNotifs] = useState(null);
  const vid = club.visit?.gov_visit_id;
  const status = clubStatus(club);

  useEffect(() => {
    if (vid) {
      api.get(`/gov/visits/${vid}/invitations`).then((r) => setInvs(r.data.invitations || [])).catch(() => setInvs([]));
      api.get(`/gov/visits/${vid}/notifications`).then((r) => setNotifs(r.data.notifications || [])).catch(() => setNotifs([]));
    } else if (canInitiate) {
      api.get(`/gov/clubs/${club.club_id}/notify-recipients`).then((r) => setRecipients(r.data.recipients || [])).catch(() => setRecipients([]));
    }
  }, [vid, club.club_id, canInitiate]);

  const addExtra = () => {
    const e = extra.trim().toLowerCase();
    if (!validEmail(e)) { toast.error("Enter a valid email"); return; }
    if ((recipients || []).some((r) => r.email === e)) { toast.error("Already in the list"); return; }
    setRecipients([...(recipients || []), { role: "Added", name: "Official", email: e }]);
    setExtra("");
  };
  const removeRecipient = (i) => setRecipients(recipients.filter((_, j) => j !== i));

  const initiate = async () => {
    if (!date) { toast.error("Enter the confirmed visit date"); return; }
    setBusy(true);
    try {
      const cr = await api.post("/gov/visits", { club_id: club.club_id, scheduled_start_utc: `${date}T04:30:00+00:00` });
      const newVid = cr.data.gov_visit_id;
      await api.post(`/gov/visits/${newVid}/team`).catch(() => {});
      await api.post(`/gov/visits/${newVid}/invitations`, { intended_role: "President" }).catch(() => {});
      await api.post(`/gov/visits/${newVid}/invitations`, { intended_role: "Secretary" }).catch(() => {});
      let sent = 0, wa = 0;
      try { const n = await api.post(`/gov/visits/${newVid}/notify-officials`, { recipients }); sent = n.data.notified_count || 0; wa = n.data.whatsapp_sent || 0; } catch (_) {}
      toast.success(`Visit initiated · ${sent} emailed${wa ? ` · WhatsApp ${wa}` : ""}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not initiate"); }
    setBusy(false);
  };
  const resend = async (invId) => {
    try { const r = await api.post(`/gov/invitations/${invId}/regenerate`); toast.success(r.data.email_delivery === "sent" ? "Invitation re-sent" : "Invitation renewed"); if (vid) api.get(`/gov/visits/${vid}/invitations`).then((x) => setInvs(x.data.invitations || [])); }
    catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  const body = (
    <>
      <div className="mb-4"><StatusPill status={status} /></div>

      <Card className="mb-4">
        <Overline>Club officials</Overline>
        <div className="mt-3 space-y-2">
          {["president", "secretary"].map((role) => (
            <div key={role} className="flex items-center gap-3">
              <Avatar icon={User} />
              <span className="flex-1 text-[15px]" style={{ color: C.ink }}>
                <b className="capitalize">{role}</b> · {club[role] || <span style={{ color: C.cranberry }}>not linked</span>}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {status === "not_initiated" && canInitiate && (
        <Card className="mb-4">
          <h3 className="font-display text-[16px] font-medium tracking-tight" style={{ color: C.ink }}>Initiate the Official Visit</h3>
          <label className="mb-1.5 mt-4 block text-[14px] font-medium" style={{ color: C.ink }}>Confirmed visit date</label>
          <DateField value={date} onChange={setDate} testid="visit-date" placeholder="Select the confirmed date"
            className="flex w-full items-center gap-2.5 rounded-2xl border bg-white px-3.5 text-[16px] outline-none"
            style={{ borderColor: C.line, minHeight: 52, color: date ? C.ink : C.sub }} />
          <div className="mt-5">
            <Overline className="mb-2">Who will be notified</Overline>
            {recipients === null ? <Loader /> : (
              <RecipientList recipients={recipients} onRemove={removeRecipient} extra={extra} setExtra={setExtra} onAdd={addExtra} />
            )}
          </div>
          <PrimaryButton data-testid="initiate-btn" className="mt-5 w-full" onClick={initiate} disabled={busy || recipients === null}>
            <PaperPlaneTilt size={16} weight="bold" /> Initiate &amp; notify {recipients ? `(${recipients.length})` : ""}
          </PrimaryButton>
        </Card>
      )}

      {vid && notifs !== null && notifs.length > 0 && (
        <Card className="mb-4">
          <Overline>Notification delivery</Overline>
          <div className="mt-3 divide-y" style={{ borderColor: C.line }} data-testid="notification-status">
            {notifs.map((n) => (
              <div key={n.notification_id} className="flex items-center gap-3 py-2.5" data-testid={`notif-${n.recipient_email}`}>
                <Avatar label={n.recipient_name} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium" style={{ color: C.ink }}>{n.role}</span>
                  <span className="block truncate text-[13px]" style={{ color: C.sub }}>{n.recipient_email}</span>
                </span>
                <StatusPill status={n.status} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {vid && (
        <Card className="mb-4">
          <Overline>Invitations</Overline>
          <div className="mt-3">
            {invs === null ? <Loader /> : invs.length === 0 ? <p className="text-[14px]" style={{ color: C.sub }}>None yet.</p> : invs.map((iv) => (
              <div key={iv.invitation_id} className="flex items-center justify-between gap-2 py-2" data-testid={`inv-${iv.intended_role}`}>
                <span className="text-[15px]" style={{ color: C.ink }}><b>{iv.intended_role}</b> · <span style={{ color: C.sub }}>{iv.status}</span></span>
                {status !== "completed" && canInitiate && (
                  <button data-testid={`resend-${iv.intended_role}`} onClick={() => resend(iv.invitation_id)} className="inline-flex items-center gap-1 text-[14px] font-medium" style={{ color: C.azure }}>
                    <ArrowClockwise size={14} weight="bold" /> Resend
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );

  if (inline) return <div data-testid="club-invitations">{body}</div>;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose} data-testid="club-drawer">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-[28px] bg-[#F5F7FB] p-5 pb-safe shadow-2xl sm:max-w-md sm:rounded-[28px] sm:p-6"
        style={{ animation: "none" }} onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full sm:hidden" style={{ background: C.line }} />
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-[21px] font-bold tracking-tight" style={{ color: C.ink }}>{club.club_name}</h2>
            <p className="mt-0.5 text-[14px]" style={{ color: C.sub }}>{club.club_id} · {club.zone_name || "No zone"}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white"><X size={18} weight="bold" /></button>
        </div>
        {body}
        {status !== "not_initiated" && (
          <GhostButton data-testid="open-report" className="w-full" onClick={() => onOpenReport ? onOpenReport() : navigate(`/review/${club.club_id}`)}>
            <ClipboardText size={16} weight="bold" /> Open Club GOV Report
          </GhostButton>
        )}
      </div>
    </div>
  );
}

export default function GovVisits() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("all");
  const [sel, setSel] = useState(null);

  const load = () => api.get("/dashboard/district").then((r) => setData(r.data)).catch(() => setData({ zones: [], can_initiate: false }));
  useEffect(() => { load(); }, []);

  const zones = data?.zones || [];
  const counts = useMemo(() => {
    const c = { completed: 0, in_progress: 0, not_initiated: 0 };
    zones.forEach((z) => (z.clubs || []).forEach((cl) => { c[clubStatus(cl)]++; }));
    return c;
  }, [zones]);
  const total = counts.completed + counts.in_progress + counts.not_initiated;
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return zones
      .filter((z) => zone === "all" || z.zone_id === zone)
      .map((z) => ({ ...z, clubs: z.clubs.filter((c) => !ql || (c.club_name || "").toLowerCase().includes(ql) || (c.club_id || "").toLowerCase().includes(ql)) }))
      .filter((z) => z.clubs.length > 0);
  }, [zones, q, zone]);

  const hero = (
    <PageHero title="GOV visits" subtitle="Find a club, review its officials and initiate the Governor's Official Visit."
      stats={data ? [
        { value: total, label: "Clubs" },
        { value: counts.completed, label: "Completed" },
        { value: counts.in_progress, label: "In progress" },
        { value: counts.not_initiated, label: "Not started" },
      ] : null} />
  );

  if (!data) return <div className="mx-auto w-full max-w-[1200px] space-y-6">{hero}<Loader /></div>;

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6">
      {hero}
      <div className="mx-auto w-full max-w-3xl space-y-5">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <div className="flex flex-1 items-center gap-2.5 rounded-2xl bg-white px-4 shadow-[0_4px_20px_rgba(28,42,58,0.04)]" style={{ minHeight: 52 }}>
          <MagnifyingGlass size={19} style={{ color: C.sub }} />
          <input data-testid="club-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by club name or ID" className="flex-1 bg-transparent text-[16px] outline-none" />
        </div>
        <select data-testid="zone-select" value={zone} onChange={(e) => setZone(e.target.value)}
          className="rounded-2xl bg-white px-4 text-[16px] font-medium shadow-[0_4px_20px_rgba(28,42,58,0.04)] outline-none" style={{ color: C.ink, minHeight: 52 }}>
          <option value="all">All zones</option>
          {zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone_name}</option>)}
        </select>
      </div>

      {filtered.length === 0 && <Card><p className="text-center text-[15px]" style={{ color: C.sub }}>No clubs match your search.</p></Card>}

      {filtered.map((z) => (
        <div key={z.zone_id}>
          <Overline className="mb-2.5 ml-0.5">{z.zone_name}</Overline>
          <Card pad={false} className="divide-y overflow-hidden">
            {z.clubs.map((c) => (
              <Row key={c.club_id} data-testid={`club-row-${c.club_id}`} onClick={() => setSel(c)} className="py-3">
                <Avatar icon={Buildings} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[16px] font-medium" style={{ color: C.ink }}>{c.club_name}</span>
                  <span className="block text-[13px]" style={{ color: C.sub }}>{c.club_id}</span>
                </span>
                <StatusPill status={clubStatus(c)} />
                <CaretRight size={17} weight="bold" style={{ color: C.sub }} />
              </Row>
            ))}
          </Card>
        </div>
      ))}
      </div>

      {sel && <ClubDrawer club={sel} canInitiate={data.can_initiate} onClose={() => setSel(null)}
        onChanged={() => { setSel(null); load(); }} />}
    </div>
  );
}
